import { Component, Inject } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { CommonModule } from '@angular/common';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatButtonModule } from '@angular/material/button';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatCardModule } from '@angular/material/card';
import { MatMenuModule } from '@angular/material/menu';
import { FormsModule } from '@angular/forms';
import { TranslateModule } from '@ngx-translate/core';
import { GalaxyFormFieldModule } from '@vendasta/galaxy/form-field';
import { GalaxyTooltipModule } from '@vendasta/galaxy/tooltip';

export interface CustomQuestion {
  id: string;
  label: string;
  answer?: string;
}

export interface Attendee {
  id: string;
  name: string;
  email: string;
  status: 'confirmed' | 'pending' | 'declined';
  registrationDate: Date;
  phoneNumber?: string;
  company?: string;
  comments?: string;
  customQuestions?: CustomQuestion[];
}

export interface AttendeeListDialogData {
  attendees?: Attendee[];
  meetingId?: string;
  meetingTitle?: string;
}

@Component({
  selector: 'meeting-scheduler-attendee-list',
  templateUrl: './attendee-list.component.html',
  styleUrls: ['./attendee-list.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    MatIconModule,
    MatInputModule,
    GalaxyFormFieldModule,
    MatButtonModule,
    MatExpansionModule,
    MatCheckboxModule,
    MatCardModule,
    MatMenuModule,
    GalaxyTooltipModule,
    FormsModule,
    TranslateModule,
  ],
})
export class AttendeeListComponent {
  searchTerm = '';
  filteredAttendees: Attendee[] = [];
  selectedAttendees: string[] = [];
  sortBy: 'name' | 'email' | 'status' | 'registrationDate' = 'name';
  sortDirection: 'asc' | 'desc' = 'asc';

  constructor(
    public dialogRef: MatDialogRef<AttendeeListComponent>,
    @Inject(MAT_DIALOG_DATA) public data: AttendeeListDialogData,
  ) {
    // For demonstration, always use sample data with custom questions
    this.filteredAttendees = [];
    this.sortAttendees();
  }

  onSearchChange(): void {
    if (!this.searchTerm.trim()) {
      this.filteredAttendees = this.data.attendees || [];
    } else {
      this.filteredAttendees = (this.data.attendees || []).filter(
        (attendee) =>
          attendee.name?.toLowerCase().includes(this.searchTerm.toLowerCase()) ||
          attendee.email?.toLowerCase().includes(this.searchTerm.toLowerCase()) ||
          attendee.company?.toLowerCase().includes(this.searchTerm.toLowerCase()),
      );
    }
    this.sortAttendees();
  }

  onSort(column: 'name' | 'email' | 'status' | 'registrationDate'): void {
    if (this.sortBy === column) {
      this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc';
    } else {
      this.sortBy = column;
      this.sortDirection = 'asc';
    }
    this.sortAttendees();
  }

  private sortAttendees(): void {
    this.filteredAttendees.sort((a, b) => {
      let aValue: any = a[this.sortBy];
      let bValue: any = b[this.sortBy];

      if (this.sortBy === 'registrationDate') {
        aValue = new Date(aValue).getTime();
        bValue = new Date(bValue).getTime();
      } else {
        aValue = aValue?.toLowerCase() || '';
        bValue = bValue?.toLowerCase() || '';
      }

      if (this.sortDirection === 'asc') {
        return aValue > bValue ? 1 : -1;
      } else {
        return aValue < bValue ? 1 : -1;
      }
    });
  }

  onAttendeeSelect(attendeeId: string): void {
    const index = this.selectedAttendees.indexOf(attendeeId);
    if (index > -1) {
      this.selectedAttendees.splice(index, 1);
    } else {
      this.selectedAttendees.push(attendeeId);
    }
  }

  onSelectAll(): void {
    if (this.selectedAttendees.length === this.filteredAttendees.length) {
      this.selectedAttendees = [];
    } else {
      this.selectedAttendees = this.filteredAttendees.map((a) => a.id);
    }
  }

  onExportAttendees(): void {
    // TODO: Implement export functionality
    console.log('Exporting attendees:', this.selectedAttendees);
  }

  onSendReminder(): void {
    // TODO: Implement reminder functionality
    console.log('Sending reminder to:', this.selectedAttendees);
  }

  onRemoveAttendees(): void {
    // TODO: Implement remove functionality
    console.log('Removing attendees:', this.selectedAttendees);
  }

  getStatusColor(status: string): string {
    switch (status) {
      case 'confirmed':
        return 'success';
      case 'pending':
        return 'warning';
      case 'declined':
        return 'error';
      default:
        return 'default';
    }
  }

  getStatusIcon(status: string): string {
    switch (status) {
      case 'confirmed':
        return 'check_circle';
      case 'pending':
        return 'schedule';
      case 'declined':
        return 'cancel';
      default:
        return 'help';
    }
  }

  onClose(): void {
    this.dialogRef.close();
  }

  trackByAttendeeId(index: number, attendee: Attendee): string {
    return attendee.id;
  }

  stopPropagation(event: Event): void {
    event.stopPropagation();
  }

  onDeleteAttendee(attendeeId: string, event: Event): void {
    event.stopPropagation();
    // TODO: Implement delete functionality
    console.log('Deleting attendee:', attendeeId);
  }
}
